import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  FaBook,
  FaPaperPlane,
  FaSpinner,
  FaInfoCircle,
  FaSave,
} from "react-icons/fa";
import { useAuth } from "../../../context/AuthContext";
import PageHeader from "../../../core/common/header/PageHeader";
import StudentAnswersInput from "./StudentAnswersInput";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

type Task = {
  taskId: number;
  taskType: string;
  taskOrder: number;
  taskDescription: string;
  audioReference: string | null;
  points: number;
  questionCount: number;
};

type Homework = {
  homeworkId: number;
  title: string;
  level: string;
  chapterNumber: number;
  instructions: string | null;
  completed: boolean;
  score: number | null;
  tasks: Task[];
};

const taskTypeLabels: Record<string, string> = {
  BEISPIEL_SATZ: "Beispiel Satz (Example Sentences)",
  SATZBAU: "Satzbau (Sentence Construction)",
  ERGANZUNGEN: "Ergänzungen (Fill in the Blanks)",
  HOREN: "Hören (Listening)",
  SCHREIBEN: "Schreiben (Writing)",
};

const StudentHomeworkCompletePage: React.FC = () => {
  const { homeworkId } = useParams<{ homeworkId: string }>();
  const { student } = useAuth();
  const navigate = useNavigate();

  const [homework, setHomework] = useState<Homework | null>(null);
  const [answers, setAnswers] = useState<Record<number, string>>({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  // ✅ Add refs for synchronous submission tracking
  const submittingRef = useRef(false);
  const hasSubmittedRef = useRef(false);

  useEffect(() => {
    fetchHomework();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [homeworkId]);

  const fetchHomework = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/student/homework/${homeworkId}`,
        { credentials: "include" }
      );

      if (response.ok) {
        const data = await response.json();
        setHomework(data);

        if (data.completed) {
          navigate(`/student/homework/${homeworkId}/submission`);
          return;
        }

        const initialAnswers: Record<number, string> = {};
        data.tasks.forEach((task: Task) => {
          initialAnswers[task.taskId] = "";
        });
        setAnswers(initialAnswers);
      } else {
        setError("Failed to load homework");
      }
    } catch (err) {
      console.error("Error fetching homework:", err);
      setError("Network error");
    } finally {
      setLoading(false);
    }
  };

  const handleAnswerChange = (taskId: number, value: string) => {
    setAnswers((prev) => ({
      ...prev,
      [taskId]: value,
    }));
  };

  const validateAnswers = () => {
    if (!homework) return false;

    for (const task of homework.tasks) {
      const answer = answers[task.taskId];

      // Check if answer exists
      if (!answer || answer.trim().length === 0) {
        setError(`Please answer Task ${task.taskOrder}`);
        return false;
      }

      // For writing tasks, check minimum length
      if (task.taskType === "SCHREIBEN" && answer.trim().length < 5) {
        setError(
          `Task ${task.taskOrder}: Answer must be at least 5 characters long`
        );
        return false;
      }

      // For multiple answer tasks, validate JSON format
      if (task.taskType === "HOREN" || task.taskType === "ERGANZUNGEN") {
        try {
          const parsed = JSON.parse(answer);
          if (!Array.isArray(parsed)) {
            setError(`Task ${task.taskOrder}: Invalid answer format`);
            return false;
          }
          // Check if all answers are filled
          const emptyCount = parsed.filter(
            (a: string) => !a || a.trim() === ""
          ).length;
          if (emptyCount > 0) {
            setError(`Task ${task.taskOrder}: Please fill all answer boxes`);
            return false;
          }
        } catch (e) {
          setError(`Task ${task.taskOrder}: Invalid answer format`);
          return false;
        }
      }
    }
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // ✅ CRITICAL: Check refs first (synchronous check)
    if (submittingRef.current || hasSubmittedRef.current) {
      console.log("Submission already in progress or completed");
      return;
    }

    // ✅ Set refs immediately (before any async operations)
    submittingRef.current = true;
    setSubmitting(true);
    setError("");

    if (!validateAnswers()) {
      submittingRef.current = false;
      setSubmitting(false);
      return;
    }

    try {
      const taskAnswers = homework!.tasks.map((task) => ({
        taskId: task.taskId,
        answer: answers[task.taskId].trim(),
      }));

      const response = await fetch(
        `${API_BASE_URL}/api/student/homework/${homeworkId}/submit`,
        {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            taskAnswers,
          }),
        }
      );

      if (response.ok) {
        // ✅ Mark as submitted permanently
        hasSubmittedRef.current = true;

        alert("✅ Homework submitted successfully! Redirecting to results...");

        setTimeout(() => {
          window.location.href = `/student/homework/${homeworkId}/submission`;
        }, 500);
      } else {
        const data = await response.json();
        setError(data.error || "Failed to submit homework");

        // ✅ Check if it's a duplicate submission error
        if (
          data.error?.includes("already submitted") ||
          data.error?.toLowerCase().includes("duplicate")
        ) {
          // Mark as submitted and redirect
          hasSubmittedRef.current = true;

          setTimeout(() => {
            navigate(`/student/homework/${homeworkId}/submission`);
          }, 2000);
        } else {
          // Reset only for other errors
          submittingRef.current = false;
          setSubmitting(false);
        }
      }
    } catch (err) {
      console.error("Error submitting homework:", err);
      setError("Network error. Please try again.");
      submittingRef.current = false;
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <FaSpinner className="animate-spin text-[#009fe3] text-4xl" />
      </div>
    );
  }

  if (!homework) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 text-lg mb-4">Homework not found</p>
          <button
            onClick={() => navigate("/student/my-homework")}
            className="px-6 py-3 bg-[#009fe3] text-white rounded-lg font-semibold hover:bg-cyan-600 transition-colors"
          >
            Back to Homework
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <PageHeader
        icon={<FaBook />}
        title="Complete Homework"
        breadcrumb={[
          { label: "Home", to: "/" },
          { label: "Dashboard", to: "/student/student-dashboard" },
          { label: "Homework", to: "/student/my-homework" },
          { label: "Complete" },
        ]}
      />

      {/* Content */}
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        {/* Homework Info */}
        <div className="bg-gradient-to-r from-blue-50 to-cyan-50 border border-blue-200 rounded-xl p-6 mb-6 shadow-sm">
          <h2 className="text-lg font-bold text-gray-900 mb-3 flex items-center gap-2">
            <FaInfoCircle className="text-blue-600" />
            Homework Information
          </h2>
          <div className="space-y-2">
            <div>
              <span className="font-semibold text-gray-700">Title: </span>
              <span className="text-gray-900">{homework.title}</span>
            </div>
            <div className="flex items-center gap-4 flex-wrap">
              <div>
                <span className="font-semibold text-gray-700">Level: </span>
                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-800">
                  {homework.level}
                </span>
              </div>
              <div>
                <span className="font-semibold text-gray-700">Chapter: </span>
                <span className="inline-flex items-center justify-center w-8 h-8 bg-blue-100 text-blue-800 font-bold rounded-md text-sm">
                  {homework.chapterNumber}
                </span>
              </div>
              <div>
                <span className="font-semibold text-gray-700">Tasks: </span>
                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-purple-100 text-purple-800">
                  {homework.tasks.length} tasks
                </span>
              </div>
              <div>
                <span className="font-semibold text-gray-700">
                  Total Points:{" "}
                </span>
                <span className="font-bold text-gray-900">20</span>
              </div>
            </div>
          </div>

          {homework.instructions && (
            <div className="mt-4 p-4 bg-white border border-blue-200 rounded-lg">
              <h4 className="text-sm font-bold text-gray-900 mb-2">
                Instructions:
              </h4>
              <p className="text-sm text-gray-700 whitespace-pre-wrap">
                {homework.instructions}
              </p>
            </div>
          )}
        </div>

        {/* Tasks Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          {homework.tasks.map((task, index) => {
            const taskType = task.taskType;
            const isMultipleAnswerTask =
              taskType === "HOREN" || taskType === "ERGANZUNGEN";

            const questionCount = task.questionCount || 1;

            return (
              <div
                key={task.taskId}
                className="bg-white rounded-xl shadow-md p-6 border border-gray-200"
              >
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-bold text-gray-900">
                    Task {task.taskOrder}
                  </h3>
                  <div className="flex items-center gap-3">
                    <span className="inline-flex items-center px-3 py-1 rounded-md text-xs font-semibold bg-purple-100 text-purple-800">
                      {taskTypeLabels[task.taskType] || task.taskType}
                    </span>
                    <span className="inline-flex items-center px-3 py-1 rounded-md text-xs font-semibold bg-green-100 text-green-800">
                      {task.points} points
                    </span>
                  </div>
                </div>

                {/* Task Description */}
                <div className="mb-4 p-4 bg-gray-50 border border-gray-200 rounded-lg">
                  <h4 className="text-sm font-semibold text-gray-700 mb-2">
                    Question:
                  </h4>
                  <p className="text-sm text-gray-800 whitespace-pre-wrap">
                    {task.taskDescription}
                  </p>
                </div>

                {/* Audio Reference for HOREN */}
                {task.taskType === "HOREN" && task.audioReference && (
                  <div className="mb-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                    <h4 className="text-sm font-semibold text-gray-700 mb-2">
                      🎧 Audio Reference:
                    </h4>
                    <p className="text-sm text-gray-800">
                      {task.audioReference}
                    </p>
                    <p className="text-xs text-gray-600 mt-2">
                      Please listen to the audio from your Kursbuch/Arbeitsbuch
                      before answering.
                    </p>
                  </div>
                )}

                {/* Answer Input */}
                <div>
                  {isMultipleAnswerTask ? (
                    <>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Your Answers * (Write in German / জার্মান ভাষায় লিখুন)
                      </label>
                      <StudentAnswersInput
                        taskId={task.taskId}
                        taskType={task.taskType}
                        questionCount={questionCount}
                        value={answers[task.taskId] || ""}
                        onChange={(value: string) =>
                          handleAnswerChange(task.taskId, value)
                        }
                      />
                    </>
                  ) : (
                    <>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Your Answer * (Write in German / জার্মান ভাষায় লিখুন)
                      </label>
                      <textarea
                        value={answers[task.taskId] || ""}
                        onChange={(e) =>
                          handleAnswerChange(task.taskId, e.target.value)
                        }
                        placeholder="Write your answer here in German... / এখানে জার্মান ভাষায় আপনার উত্তর লিখুন..."
                        rows={6}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009fe3] focus:border-transparent transition-all text-sm resize-none"
                        style={{ fontSize: "16px" }}
                        required
                      />
                      <div className="text-xs text-gray-500 mt-1">
                        {(answers[task.taskId] || "").length} characters
                        (minimum 5 characters required)
                      </div>
                    </>
                  )}
                </div>
              </div>
            );
          })}

          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
              {error}
            </div>
          )}

          {/* Final Warning */}
          <div className="bg-gradient-to-r from-red-50 to-orange-50 border-2 border-red-400 rounded-xl p-5 shadow-lg">
            <div className="flex items-start gap-3">
              <FaSave className="text-red-600 text-2xl mt-1" />
              <div className="flex-1">
                <h3 className="text-lg font-bold text-red-900 mb-2">
                  ⚠️ Before You Submit!
                </h3>
                <div className="space-y-1.5 text-sm text-red-800">
                  <p className="font-semibold">
                    ✅ Double-check all your answers
                  </p>
                  <p>
                    ✅ Make sure you've answered all {homework.tasks.length}{" "}
                    tasks
                  </p>
                  <p className="font-semibold text-red-900 mt-2">
                    ⚠️ You can only submit once - no changes allowed after
                    submission!
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Submit Buttons */}
          <div className="flex flex-col sm:flex-row gap-3">
            <button
              type="submit"
              disabled={submitting || hasSubmittedRef.current}
              className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-[#009fe3] to-cyan-500 text-white rounded-lg font-semibold hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
            >
              {submitting ? (
                <>
                  <FaSpinner className="animate-spin" />
                  Submitting & Evaluating...
                </>
              ) : (
                <>
                  <FaPaperPlane />
                  Submit Homework
                </>
              )}
            </button>
            <button
              type="button"
              onClick={() => navigate("/student/my-homework")}
              disabled={submitting}
              className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg font-semibold hover:bg-gray-300 disabled:opacity-50 transition-colors"
            >
              Cancel
            </button>
          </div>
        </form>
      </main>
    </div>
  );
};

export default StudentHomeworkCompletePage;
